import moment from 'moment';
import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { icons } from '~/assets';
import Button from '~/components/button';
import Container from '~/components/container';
import Dialog from '~/components/dialog';
import Loading from '~/components/loading';
import useApi, { ApiMethod } from '~/hooks/useApi';
import Endpoints from '~/services/endpoints';
import {
  CreateDocumentSolicitationRequest,
  DocumentSolicitation,
  DocumentSolicitationStatus,
  DownloadDocumentSolicitationResponse,
} from '~/services/files/documentSolicitation/types';
import Constants from '~/utils/constants';
import { DocumentsClientDetail } from '..';
import * as St from './styles';

interface LocationState {
  client?: DocumentsClientDetail;
}

const PROCESSING_STATUSES: DocumentSolicitationStatus[] = [
  'PROCESSANDO',
  'AGUARDANDO_DOCUMENTOS',
  'AGUARDANDO_ZIP',
  'PROCESSANDO_ZIP',
];

const formatCpf = (cpf?: string) => {
  const value = cpf?.replace(/\D/g, '');

  if (!value || value.length !== 11) return cpf || '-';

  return value.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
};

const formatCardNumber = (cardNumber?: string) => {
  const value = cardNumber?.replace(/\D/g, '');

  if (!value) return '-';

  return value.replace(/(\d{4})(?=\d)/g, '$1.');
};

const DocumentsDetails = (): JSX.Element => {
  const history = useHistory();
  const { state } = useLocation<LocationState>();
  const client = state?.client;
  const [
    activeSolicitation,
    setActiveSolicitation,
  ] = React.useState<DocumentSolicitation | null>(null);
  const [availabilityCheckFailed, setAvailabilityCheckFailed] = React.useState(
    false,
  );
  const [checkingAvailability, setCheckingAvailability] = React.useState(
    Boolean(client?.cpf),
  );
  const [dialog, setDialog] = React.useState<{
    open: boolean;
    success: boolean;
    message: string;
  }>({ open: false, success: false, message: '' });

  const createSolicitation = useApi<DocumentSolicitation>(
    Endpoints.files.documentSolicitations,
    ApiMethod.POST,
  );
  const listSolicitations = useApi<DocumentSolicitation[]>(
    Endpoints.files.documentSolicitationsByCpf(
      client?.cpf?.replace(/\D/g, '') || '',
    ),
    ApiMethod.GET,
  );
  const downloadSolicitation = useApi<DownloadDocumentSolicitationResponse>(
    Endpoints.files.documentSolicitationDownload,
    ApiMethod.POST,
  );

  React.useEffect(() => {
    const cpf = client?.cpf?.replace(/\D/g, '');

    if (!cpf) return;

    const checkActiveSolicitation = async () => {
      setCheckingAvailability(true);
      const response = await listSolicitations.callApi();

      if (response.status === 200 && Array.isArray(response.data)) {
        const solicitation = response.data
          .filter(
            (item) =>
              item.cpf?.replace(/\D/g, '') === cpf &&
              (item.status === 'PROCESSADO' ||
                PROCESSING_STATUSES.includes(item.status)) &&
              moment(item.dataExpiracao).isAfter(moment()),
          )
          .sort((first, second) =>
            moment(second.dataSolicitacao).diff(moment(first.dataSolicitacao)),
          )[0];

        setActiveSolicitation(solicitation || null);
        setAvailabilityCheckFailed(false);
        setCheckingAvailability(false);
        return;
      }

      setAvailabilityCheckFailed(true);
      setCheckingAvailability(false);
      setDialog({
        open: true,
        success: false,
        message:
          response.mensagem ||
          'Não foi possível verificar as solicitações existentes.',
      });
    };

    checkActiveSolicitation();
    // A verificação ocorre somente ao entrar nos detalhes do cliente.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client?.cpf]);

  const requestFile = async () => {
    const cpf = client?.cpf?.replace(/\D/g, '');
    const idSolicitante = localStorage.getItem(Constants.USER_ID);
    const storedUser = localStorage.getItem(Constants.USER);
    const user = storedUser ? JSON.parse(storedUser) : null;
    const nomeSolicitante =
      user?.nomeTitular || user?.nome || user?.nomeCompleto;

    if (!cpf || !idSolicitante || !nomeSolicitante) {
      setDialog({
        open: true,
        success: false,
        message: !cpf
          ? 'Não foi possível identificar o CPF do cliente.'
          : 'Não foi possível identificar o usuário solicitante.',
      });
      return;
    }

    const payload: CreateDocumentSolicitationRequest = {
      cpf,
      idSolicitante,
      nomeSolicitante,
    };
    const response = await createSolicitation.callApi(payload);

    if (response.status === 202) {
      setDialog({
        open: true,
        success: true,
        message:
          'Solicitação recebida. O pacote de documentos está sendo processado.',
      });
      return;
    }

    setDialog({
      open: true,
      success: false,
      message:
        response.mensagem ||
        'Não foi possível solicitar o pacote de documentos. Tente novamente.',
    });
  };

  const downloadFile = async () => {
    const idSolicitante = localStorage.getItem(Constants.USER_ID);

    if (!activeSolicitation || !idSolicitante) {
      setDialog({
        open: true,
        success: false,
        message: 'Não foi possível identificar o arquivo para download.',
      });
      return;
    }

    const response = await downloadSolicitation.callApi({
      id: activeSolicitation.id,
      idSolicitante,
    });

    if (response.status === 200 && response.data?.urlDownload) {
      const link = document.createElement('a');
      link.href = response.data.urlDownload;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      link.remove();
      return;
    }

    setDialog({
      open: true,
      success: false,
      message:
        response.mensagem ||
        (response.status === 410
          ? 'O arquivo expirou. Faça uma nova solicitação.'
          : 'Não foi possível gerar o link de download.'),
    });
  };

  const documentsAvailable = activeSolicitation?.status === 'PROCESSADO';
  const documentsProcessing = Boolean(
    activeSolicitation &&
      PROCESSING_STATUSES.includes(activeSolicitation.status),
  );

  let requestTitle = 'Gerar pacote completo de documentos';
  let requestDescription =
    'Inclui documentos, faturas, imagens, contratos e Excel de transações. Você pode sair da tela enquanto o arquivo é montado.';

  if (documentsAvailable) {
    requestTitle = 'Documentos disponíveis';
    requestDescription =
      'Já existe um pacote válido para este CPF. O arquivo está disponível para download.';
  } else if (documentsProcessing) {
    requestTitle = 'Documentos em processamento';
    requestDescription =
      'Já existe uma solicitação válida para este CPF. Recarregue a página mais tarde para consultar a disponibilidade.';
  }

  const renderRequestAction = () => {
    if (documentsAvailable) {
      return (
        <Button
          fitContent
          onClick={downloadFile}
          loading={downloadSolicitation.loading}
          disabled={downloadSolicitation.loading}
        >
          <span>Baixar documentos</span>
        </Button>
      );
    }

    if (documentsProcessing) {
      return (
        <Button fitContent disabled>
          <span>Aguarde...</span>
        </Button>
      );
    }

    return (
      <Button
        fitContent
        onClick={requestFile}
        loading={createSolicitation.loading}
        disabled={
          !client?.cpf ||
          createSolicitation.loading ||
          listSolicitations.loading ||
          checkingAvailability ||
          availabilityCheckFailed
        }
      >
        <span>Solicitar arquivo</span>
      </Button>
    );
  };

  return (
    <>
      <Loading
        status={
          createSolicitation.loading ||
          listSolicitations.loading ||
          downloadSolicitation.loading ||
          checkingAvailability
        }
      />
      <Dialog
        icon={dialog.success ? icons.confirmation : icons.exclamation}
        open={dialog.open}
        positiveLabel={dialog.success ? 'Ir para o histórico' : 'Entendi'}
        positiveAction={() => {
          if (dialog.success) {
            history.push('/file/documents/history');
            return;
          }
          setDialog((current) => ({ ...current, open: false }));
        }}
        message={dialog.message}
      />

      <Container>
        <St.PageTitle>Detalhes</St.PageTitle>

        <St.Card>
          <St.CardContent>
            <div>
              <St.SectionTitle>Detalhes do cliente</St.SectionTitle>
              <St.DataGrid>
                <St.DataItem>
                  <St.Label>Nome</St.Label>
                  <St.Value>{client?.nome || '-'}</St.Value>
                </St.DataItem>
                <St.DataItem>
                  <St.Label>CPF</St.Label>
                  <St.Value>{formatCpf(client?.cpf)}</St.Value>
                </St.DataItem>
              </St.DataGrid>
            </div>
            <Button fitContent onClick={() => history.goBack()}>
              <span>Voltar</span>
            </Button>
          </St.CardContent>
        </St.Card>

        <St.Card>
          <St.CardContent>
            <div>
              <St.RequestEyebrow>
                Solicitar pacote de documentos
              </St.RequestEyebrow>
              <St.RequestTitle>{requestTitle}</St.RequestTitle>
              <St.RequestDescription>
                {requestDescription}
              </St.RequestDescription>
            </div>
            {renderRequestAction()}
          </St.CardContent>
        </St.Card>
      </Container>
    </>
  );
};

export default DocumentsDetails;
